import { useEffect, useState, type FormEvent } from 'react';

import { EmptyState, ErrorState } from '../../components/ui/Feedback';
import { SortableTh } from '../../components/ui/SortableTh';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { ApiError } from '../../lib/api-client';
import { confirmUpdate } from '../../lib/confirm-service';
import { formatDateTime } from '../../lib/datetime';
import { useTableSort } from '../../lib/table-sort';
import { useBulkApplyTds, useGeneralSettings, useTdsManagement } from './settings.queries';
import { buildTdsApplyChanges, calculateTdsPreview, clampSettingsPage } from './settings.utils';
import { tdsSections } from './types';

const errorMessage = (error: unknown, fallback: string) => error instanceof ApiError ? error.message : fallback;
const money = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });

export const TdsManagementPanel = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sectionInput, setSectionInput] = useState('');
  const [billInput, setBillInput] = useState('');
  const [section, setSection] = useState('');
  const [billNumber, setBillNumber] = useState('');
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [changes, setChanges] = useState<Record<string, boolean>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const query = useTdsManagement(page, limit, section, billNumber, sortBy, sortOrder);
  const tableSort = useTableSort(sortBy, sortOrder, (next) => {
    setSortBy(next.key);
    setSortOrder(next.direction);
    setPage(1);
  });
  const general = useGeneralSettings();
  const applyMutation = useBulkApplyTds();
  const records = query.data?.data ?? [];
  const totalPages = Math.max(query.data?.pagination.totalPages ?? 1, 1);
  const tdsPercent = typeof general.data?.tdsPercent === 'number' && Number.isFinite(general.data.tdsPercent)
    ? general.data.tdsPercent
    : null;
  const generalReady = general.isSuccess && tdsPercent !== null;
  const rowsBusy = query.isFetching || applyMutation.isPending;

  useEffect(() => {
    setChanges({});
  }, [page, limit, section, billNumber, sortBy, sortOrder]);

  useEffect(() => {
    const reportedTotalPages = query.data?.pagination.totalPages;
    if (reportedTotalPages !== undefined && page !== clampSettingsPage(page, reportedTotalPages)) {
      setPage(clampSettingsPage(page, reportedTotalPages));
    }
  }, [page, query.data?.pagination.totalPages]);

  useEffect(() => {
    if (!generalReady) setChanges({});
  }, [generalReady]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setSection(sectionInput);
    setBillNumber(billInput.trim());
  };

  const save = async () => {
    if (!generalReady || rowsBusy) return;
    const pendingChanges = buildTdsApplyChanges(changes);
    if (!pendingChanges.length) return;
    const confirmed = await confirmUpdate(`the TDS selections for ${pendingChanges.length} payment${pendingChanges.length === 1 ? '' : 's'}`);
    if (!confirmed) return;
    setSaveError(null);
    try {
      await applyMutation.mutateAsync({ changes: pendingChanges });
      setChanges({});
    } catch (error) {
      setSaveError(errorMessage(error, 'The TDS selections could not be saved.'));
    }
  };

  return (
    <div className="settings-tds-panel">
      <div className="settings-list-head">
        <div><span className="settings-kicker">Payment TDS</span><h2>TDS Management</h2><p>{generalReady ? <>Search payments and apply the General Settings TDS rate of <strong>{tdsPercent}%</strong>.</> : 'Search payments while the General Settings TDS rate is being resolved.'}</p></div>
      </div>

      {general.isLoading || general.isFetching && !general.data ? (
        <div className="settings-runtime-notice" role="status">Loading the General Settings TDS rate. Apply controls are disabled.</div>
      ) : general.isError ? (
        <div className="general-settings-alert" role="alert">
          {errorMessage(general.error, 'The General Settings TDS rate could not be loaded. Apply controls are disabled.')}
          <button className="settings-edit-button" type="button" onClick={() => void general.refetch()}>Try again</button>
        </div>
      ) : !generalReady ? (
        <div className="general-settings-alert" role="alert">The TDS rate is not configured in General Settings. Apply controls are disabled.</div>
      ) : null}

      <form className="settings-tds-filters" onSubmit={submitSearch}>
        <label><span>Section</span><select value={sectionInput} onChange={(event) => setSectionInput(event.target.value)}><option value="">All sections</option>{tdsSections.map((value) => <option value={value} key={value}>{value}</option>)}</select></label>
        <label><span>Bill No.</span><input value={billInput} onChange={(event) => setBillInput(event.target.value)} placeholder="Enter bill number" /></label>
        <button className="button button-primary" type="submit">Search</button>
      </form>

      <div className="settings-table-card">
        {saveError ? <div className="inline-alert" role="alert">{saveError}</div> : null}
        {query.isLoading ? <TableSkeleton rows={6} columns={6} label="Loading TDS payments" /> : null}
        {query.isError ? <ErrorState title="Unable to load TDS payments" message={errorMessage(query.error, 'The payment list could not be loaded.')} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && !records.length ? <EmptyState title="No matching bills" message="Try a different section or bill number." /> : null}
        {query.isSuccess && records.length ? (
          <>
            <div className="table-scroll settings-table-scroll">
              <table className="settings-table">
                <thead><tr><th>#</th><SortableTh control={tableSort} column="billNumber">Bill No</SortableTh><SortableTh control={tableSort} column="section">Section</SortableTh><SortableTh control={tableSort} column="insuranceType">Insurance Type</SortableTh><SortableTh control={tableSort} column="paymentDate">Payment Date</SortableTh><SortableTh control={tableSort} column="paymentMode">Payment Mode</SortableTh><SortableTh control={tableSort} column="paymentAmount">Payment Amount</SortableTh><SortableTh control={tableSort} column="paymentTds">Payment TDS</SortableTh><th>{generalReady ? `Apply (${tdsPercent}%)` : 'Apply (unavailable)'}</th></tr></thead>
                <tbody>{records.map((record, index) => {
                  const hasPendingChange = Object.hasOwn(changes, record.id);
                  const applied = changes[record.id] ?? record.applied;
                  const paymentTds = hasPendingChange && tdsPercent !== null
                    ? applied ? calculateTdsPreview(record.paymentAmount, tdsPercent) : 0
                    : record.paymentTds;
                  return (
                    <tr key={record.id}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td><strong className="settings-primary-cell">{record.billNumber}</strong></td>
                      <td>{record.section}</td>
                      <td>{record.insuranceType || '-'}</td>
                      <td>{record.paymentDate ? formatDateTime(record.paymentDate) : '-'}</td>
                      <td>{record.paymentMode || '-'}</td>
                      <td>{money.format(record.paymentAmount)}</td>
                      <td>{money.format(paymentTds)}</td>
                      <td><label className="settings-tds-apply"><input type="checkbox" checked={applied} disabled={!generalReady || rowsBusy} onChange={(event) => setChanges((current) => ({ ...current, [record.id]: event.target.checked }))} /><span>{applied ? 'Apply' : 'Skip'}</span></label></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
            <div className="settings-pagination">
              <label><span>Rows per page</span><select value={limit} disabled={query.isFetching} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }}><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option></select></label>
              <span className="settings-page-summary">Page {page} of {totalPages}</span>
              <nav aria-label="TDS payment pages"><button type="button" disabled={page <= 1 || query.isFetching} onClick={() => setPage((value) => value - 1)} aria-label="Previous page">&lt;</button><button type="button" className="active" aria-current="page">{page}</button><button type="button" disabled={page >= totalPages || query.isFetching} onClick={() => setPage((value) => value + 1)} aria-label="Next page">&gt;</button></nav>
            </div>
          </>
        ) : null}
      </div>
      <div className="settings-tds-actions"><button className="button button-primary" type="button" disabled={!generalReady || rowsBusy || !Object.keys(changes).length} onClick={() => void save()}>{applyMutation.isPending ? 'Saving...' : 'Save / Apply'}</button></div>
    </div>
  );
};
